/**
 * Firebase Authentication Error Mapping
 *
 * Maps Firebase error codes to user-friendly messages.
 * Security: Sanitizes errors to avoid exposing sensitive data
 */

/**
 * User-friendly error messages for Firebase auth errors
 *
 * Security considerations:
 * - auth/user-not-found and auth/wrong-password return the same message
 *   to prevent attackers from enumerating valid email addresses
 * - Generic fallback messages for unknown errors
 */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  // Sign-in errors
  'auth/user-not-found': 'Invalid email or password',
  'auth/wrong-password': 'Invalid email or password',
  'auth/invalid-email': 'Invalid email address',
  'auth/user-disabled': 'This account has been disabled',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later',
  'auth/invalid-credential': 'Invalid email or password',

  // Password reset errors
  'auth/invalid-action-code': 'Invalid or expired reset link',
  'auth/expired-action-code': 'Reset link has expired',

  // Phone authentication errors
  'auth/invalid-phone-number': 'Invalid phone number format',
  'auth/missing-phone-number': 'Phone number is required',
  'auth/invalid-verification-code': 'Invalid verification code',
  'auth/invalid-verification-id': 'Verification session expired. Please try again',
  'auth/code-expired': 'Verification code has expired. Please request a new one',
  'auth/session-expired': 'Verification session expired. Please try again',
  'auth/quota-exceeded': 'SMS quota exceeded. Please try again later',
  'auth/captcha-check-failed': 'Captcha verification failed. Please try again',
  'auth/missing-code': 'Verification code is required',
  'auth/missing-verification-code': 'Verification code is required',
  'auth/missing-verification-id': 'Verification session not found. Please request a new code',

  // Network errors
  'auth/network-request-failed': 'Network error. Please check your connection',

  // General errors
  'auth/popup-closed-by-user': 'Sign-in was cancelled',
  'auth/popup-blocked': 'Pop-up was blocked. Please allow pop-ups for this site',
  'auth/cancelled-popup-request': 'Only one sign-in popup is allowed at a time',
  'auth/unauthorized-domain': 'This domain is not authorized for authentication',

  // Sign-up errors
  'auth/email-already-in-use': 'An account with this email already exists',

  // Account management
  'auth/requires-recent-login': 'Please sign in again to perform this action',
}

/**
 * Get user-friendly error message from Firebase error
 *
 * @param error - Firebase error object or any error with a code property
 * @returns User-friendly error message safe to display to users
 *
 * @example
 * try {
 *   await signInWithEmailAndPassword(auth, email, password)
 * } catch (err) {
 *   const message = getAuthErrorMessage(err)
 *   // message = "Invalid email or password"
 * }
 */
export function getAuthErrorMessage(error: any): string {
  if (!error) {
    return 'An unknown error occurred'
  }

  // Check if error has a Firebase auth error code
  if (error.code && typeof error.code === 'string') {
    const errorCode = error.code as string

    // Return mapped message if exists
    if (AUTH_ERROR_MESSAGES[errorCode]) {
      return AUTH_ERROR_MESSAGES[errorCode]
    }
  }

  // Fallback to error message if available
  if (error.message && typeof error.message === 'string') {
    // Don't expose internal Firebase error messages directly
    // Return generic message instead
    console.error('[Auth] Unmapped error:', error.code, error.message)
    return 'Authentication failed. Please try again'
  }

  // Ultimate fallback
  return 'An unexpected error occurred'
}

/**
 * Check if error is a network-related error
 * Useful for displaying specific retry UI
 */
export function isNetworkError(error: any): boolean {
  return error?.code === 'auth/network-request-failed'
}

/**
 * Check if error is due to too many requests
 * Useful for implementing rate limiting UI
 */
export function isRateLimitError(error: any): boolean {
  return error?.code === 'auth/too-many-requests'
}

/**
 * Check if error requires re-authentication
 * Useful for prompting user to sign in again
 */
export function requiresRecentLogin(error: any): boolean {
  return error?.code === 'auth/requires-recent-login'
}

/**
 * Backend API Error Messages
 * Maps common backend error codes to user-friendly messages
 */
const BACKEND_ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your input',
  401: 'Authentication failed. Please try again',
  403: 'You do not have permission to perform this action',
  404: 'Resource not found',
  409: 'This resource already exists',
  429: 'Too many requests. Please try again later',
  500: 'Server error. Please try again later',
  503: 'Service temporarily unavailable. Please try again later',
}

/**
 * Process backend API error response
 *
 * @param error - Axios error or fetch error response
 * @returns User-friendly error message
 */
export function getBackendErrorMessage(error: any): string {
  // Handle Axios error format
  if (error.response) {
    const status = error.response.status
    const data = error.response.data

    // If backend provides a message, use it (if safe)
    if (data?.message && typeof data.message === 'string') {
      // Filter out internal error details
      if (!data.message.includes('Error:') && !data.message.includes('Exception')) {
        return data.message
      }
    }

    // If backend provides error details
    if (data?.detail && typeof data.detail === 'string') {
      return data.detail
    }

    // Use mapped status code message
    if (BACKEND_ERROR_MESSAGES[status]) {
      return BACKEND_ERROR_MESSAGES[status]
    }
  }

  // Handle fetch error format
  if (error.status && BACKEND_ERROR_MESSAGES[error.status]) {
    return BACKEND_ERROR_MESSAGES[error.status]
  }

  // Network error
  if (error.message?.includes('Network') || error.message?.includes('fetch')) {
    return 'Network error. Please check your connection'
  }

  return 'An error occurred. Please try again'
}

/**
 * Centralized auth error handler
 * Processes both Firebase and backend errors
 *
 * @param error - Error from Firebase or backend API
 * @param context - Context of the error (e.g., 'signin', 'signup', 'backend')
 * @returns User-friendly error message
 */
export function handleAuthError(error: any, context: 'firebase' | 'backend' = 'firebase'): string {
  console.error(`[Auth Error - ${context}]:`, error)

  if (context === 'backend') {
    return getBackendErrorMessage(error)
  }

  return getAuthErrorMessage(error)
}
